<script lang="ts">
    import { onMount, onDestroy } from 'svelte';
    import { link, push } from 'svelte-spa-router';
    import Chart from 'chart.js/auto';
    import { api } from '../framework/js/api.js';
    import {
        AppPageHeader,
        AppLoadingState,
        AppErrorState,
    } from '../framework/components/app';
    import { relativeTime } from '../framework/components/app/assessmentFormat';

    type AgentKey = 'illuminator' | 'brief' | 'gapfinder';

    type AgentStat = { total: number; this_week: number; this_month: number };

    type Stats = Record<AgentKey, AgentStat>;

    type RecentItem = {
        agent: AgentKey;
        uuid: string;
        title: string;
        updated_at: string;
        summary: { type: 'verdict' | 'score' | 'status' | 'version'; value: string | number };
        href: string;
    };

    type WeeklyBucket = {
        week: string;
        week_start: string;
        illuminator: number;
        brief: number;
        gapfinder: number;
    };

    type AccentName = 'teal' | 'orange' | 'pink' | 'purple' | 'yellow' | 'green';

    /* Accent rotation — agents get a color slot by position.
       Order is intentional: first three slots (teal, orange, pink) are the
       primary rotation; purple/yellow/green are held for agents 4, 5, 6+. */
    const ACCENT_ROTATION: AccentName[] = ['teal', 'orange', 'pink', 'purple', 'yellow', 'green'];

    function accentFor(index: number): AccentName {
        return ACCENT_ROTATION[index % ACCENT_ROTATION.length];
    }

    const AGENTS: { key: AgentKey; name: string; icon: string; href: string; subtitle: string; accent: AccentName }[] = [
        {
            key: 'illuminator',
            name: 'Illuminator',
            icon: 'lightbulb',
            href: '/assessments',
            subtitle: 'Should it be researched?',
            accent: accentFor(0),
        },
        {
            key: 'brief',
            name: 'Built-to-Brief',
            icon: 'pencil alternate',
            href: '/brief-builder',
            subtitle: 'Nine questions. One decision-ready brief.',
            accent: accentFor(1),
        },
        {
            key: 'gapfinder',
            name: 'GapFinder',
            icon: 'search',
            href: '/gapfinder',
            subtitle: 'Evaluate your research brief.',
            accent: accentFor(2),
        },
    ];

    function agentAccent(key: AgentKey): AccentName {
        return AGENTS.find((a) => a.key === key)?.accent ?? 'teal';
    }

    function accentVar(name: AccentName, shade: 'base' | 'soft' | 'ink' = 'base'): string {
        const suffix = shade === 'base' ? '' : `-${shade}`;
        return `var(--accent-${name}${suffix})`;
    }

    let stats: Stats | null = null;
    let recent: RecentItem[] = [];
    let weekly: WeeklyBucket[] = [];
    let state: 'loading' | 'ready' | 'error' = 'loading';

    let chartCanvas: HTMLCanvasElement;
    let chartInstance: any = null;

    async function load() {
        state = 'loading';
        try {
            const [statsRes, activityRes]: any[] = await Promise.all([
                api.getDashboardStats(),
                api.getDashboardActivity(5, 90),
            ]);
            if (!statsRes?.success || !activityRes?.success) {
                state = 'error';
                return;
            }
            stats = statsRes.data as Stats;
            recent = (activityRes.data?.recent ?? []) as RecentItem[];
            weekly = (activityRes.data?.weekly ?? []) as WeeklyBucket[];
            state = 'ready';
            setTimeout(renderChart, 0);
        } catch {
            state = 'error';
        }
    }

    function renderChart() {
        if (!chartCanvas || !weekly.length) return;
        if (chartInstance) chartInstance.destroy();

        const labels = weekly.map((w) => {
            const d = new Date(w.week_start);
            return d.toLocaleDateString(navigator.language, { month: 'short', day: 'numeric' });
        });

        const cs = getComputedStyle(document.documentElement);
        const hex = (name: AccentName) => cs.getPropertyValue(`--accent-${name}`).trim();

        chartInstance = new Chart(chartCanvas, {
            type: 'bar',
            data: {
                labels,
                datasets: [
                    {
                        label: 'Illuminator',
                        data: weekly.map((w) => w.illuminator),
                        backgroundColor: hex(agentAccent('illuminator')),
                        stack: 'agents',
                        borderRadius: 4,
                    },
                    {
                        label: 'Built-to-Brief',
                        data: weekly.map((w) => w.brief),
                        backgroundColor: hex(agentAccent('brief')),
                        stack: 'agents',
                        borderRadius: 4,
                    },
                    {
                        label: 'GapFinder',
                        data: weekly.map((w) => w.gapfinder),
                        backgroundColor: hex(agentAccent('gapfinder')),
                        stack: 'agents',
                        borderRadius: 4,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'top',
                        align: 'end',
                        labels: { boxWidth: 10, boxHeight: 10, font: { size: 12 } },
                    },
                    tooltip: { mode: 'index', intersect: false },
                },
                scales: {
                    x: { stacked: true, grid: { display: false }, ticks: { font: { size: 11 } } },
                    y: {
                        stacked: true,
                        beginAtZero: true,
                        grid: { color: 'rgba(0,0,0,.06)' },
                        ticks: { stepSize: 1, font: { size: 11 } },
                    },
                },
            },
        });
    }

    function agentLabel(a: AgentKey): string {
        return AGENTS.find((x) => x.key === a)?.name ?? a;
    }

    function agentIcon(a: AgentKey): string {
        return AGENTS.find((x) => x.key === a)?.icon ?? 'circle';
    }

    function summaryText(s: RecentItem['summary']): string {
        if (s.type === 'score') return `Score ${s.value}`;
        if (s.type === 'verdict') return `Verdict: ${s.value}`;
        if (s.type === 'version') return `${s.value}`;
        return String(s.value);
    }

    function goToAgent(href: string) {
        push(href);
    }

    $: tips = buildTips(stats, recent);

    function buildTips(s: Stats | null, _r: RecentItem[]): { title: string; body: string; href: string; icon: string }[] {
        if (!s) return [];
        const out: { title: string; body: string; href: string; icon: string }[] = [];

        if (s.illuminator.total === 0) {
            out.push({
                title: 'Try Illuminator first',
                body: 'Before commissioning primary research, check if existing evidence already answers your question.',
                href: '/assessments',
                icon: 'clipboard list',
            });
        }
        if (s.brief.total === 0) {
            out.push({
                title: 'Build a decision-ready brief',
                body: 'Nine questions, under ten minutes — watch your brief assemble as you answer.',
                href: '/brief-builder',
                icon: 'file text',
            });
        }
        if (s.gapfinder.total === 0) {
            out.push({
                title: 'Evaluate an existing brief',
                body: 'Drop any brief into GapFinder to score it and find blind spots in under a minute.',
                href: '/gapfinder',
                icon: 'shield alternate',
            });
        }
        if (s.brief.total > 0 && s.gapfinder.total === 0) {
            out.push({
                title: 'Score your latest brief',
                body: 'You can run GapFinder on a brief you built with Built-to-Brief.',
                href: '/gapfinder',
                icon: 'shield alternate',
            });
        }
        if (out.length === 0) {
            out.push({
                title: 'Tech makes qual-quant research richer',
                body: 'The right research tech lets you combine qual and quant in a single study.',
                href: '/learning',
                icon: 'lightbulb outline',
            });
            out.push({
                title: 'Avoid hypothetical questions',
                body: 'Ask about past behaviours or scenarios that mimic real choices — not what someone "would" do.',
                href: '/learning',
                icon: 'lightbulb outline',
            });
            out.push({
                title: 'Define your business question',
                body: 'Always let the business question drive the methodology, not the other way round.',
                href: '/learning',
                icon: 'lightbulb outline',
            });
        }

        return out.slice(0, 3);
    }

    onMount(load);
    onDestroy(() => {
        if (chartInstance) chartInstance.destroy();
    });

    $: monthTotal = stats
        ? stats.illuminator.this_month + stats.brief.this_month + stats.gapfinder.this_month
        : 0;
</script>

<div class="user-home">
    <AppPageHeader
        title="Dashboard"
        subtitle="Your research activity at a glance."
        showAction={false}
    />

    {#if state === 'loading'}
        <AppLoadingState label="Loading dashboard" />
    {:else if state === 'error'}
        <AppErrorState on:retry={load} />
    {:else if stats}
        <section class="section">
            <h2 class="section-title">Start something new</h2>
            <div class="quickstart-grid">
                {#each AGENTS as a (a.key)}
                    <a
                        use:link
                        href={a.href}
                        class="quickstart-card"
                        aria-label={`Start ${a.name}`}
                        style="--card-accent: {accentVar(a.accent)}; --card-accent-soft: {accentVar(a.accent, 'soft')}; --card-accent-ink: {accentVar(a.accent, 'ink')};"
                    >
                        <div class="qs-icon" aria-hidden="true"><i class="{a.icon} icon"></i></div>
                        <div class="qs-body">
                            <div class="qs-name">{a.name}</div>
                            <div class="qs-subtitle">{a.subtitle}</div>
                        </div>
                        <span class="qs-cta">Start <i class="angle right icon" aria-hidden="true"></i></span>
                    </a>
                {/each}
            </div>
        </section>

        <section class="section">
            <h2 class="section-title">Activity</h2>
            <div class="stat-grid">
                {#each AGENTS as a (a.key)}
                    <button
                        type="button"
                        class="stat-card"
                        on:click={() => goToAgent(a.href)}
                        aria-label={`Open ${a.name}`}
                        style="--card-accent: {accentVar(a.accent)}; --card-accent-ink: {accentVar(a.accent, 'ink')};"
                    >
                        <div class="stat-head">
                            <i class="{a.icon} icon stat-icon" aria-hidden="true"></i>
                            <span class="stat-label">{a.name}</span>
                        </div>
                        <div class="stat-value">{stats[a.key].total}</div>
                        <div class="stat-hint">
                            {stats[a.key].this_month} this month
                            {#if stats[a.key].this_week > 0}
                                · {stats[a.key].this_week} this week
                            {/if}
                        </div>
                    </button>
                {/each}
                <div class="stat-card summary" aria-label="Total this month">
                    <div class="stat-head">
                        <i class="calendar alternate outline icon" aria-hidden="true"></i>
                        <span class="stat-label">This month</span>
                    </div>
                    <div class="stat-value">{monthTotal}</div>
                    <div class="stat-hint">across all agents</div>
                </div>
            </div>
        </section>

        <section class="section split">
            <div class="recent-col">
                <h2 class="section-title">Recent activity</h2>
                {#if recent.length === 0}
                    <div class="empty-muted">
                        No activity yet. Pick an agent above to get started.
                    </div>
                {:else}
                    <ul class="recent-list">
                        {#each recent as item (item.agent + '-' + item.uuid)}
                            {@const accent = agentAccent(item.agent)}
                            <li>
                                <a
                                    use:link
                                    href={item.href}
                                    class="recent-row"
                                    style="--row-accent: {accentVar(accent)}; --row-accent-soft: {accentVar(accent, 'soft')}; --row-accent-ink: {accentVar(accent, 'ink')};"
                                >
                                    <div class="recent-icon" aria-hidden="true">
                                        <i class="{agentIcon(item.agent)} icon"></i>
                                    </div>
                                    <div class="recent-main">
                                        <div class="recent-title">{item.title}</div>
                                        <div class="recent-meta">
                                            <span class="recent-agent">{agentLabel(item.agent)}</span>
                                            <span class="recent-sep" aria-hidden="true">·</span>
                                            <span class="recent-summary">{summaryText(item.summary)}</span>
                                        </div>
                                    </div>
                                    <span class="recent-time">{relativeTime(item.updated_at)}</span>
                                </a>
                            </li>
                        {/each}
                    </ul>
                {/if}
            </div>

            <div class="chart-col">
                <h2 class="section-title">Last 90 days</h2>
                <div class="chart-wrap">
                    <canvas bind:this={chartCanvas}></canvas>
                </div>
            </div>
        </section>

        {#if tips.length > 0}
            <section class="section">
                <h2 class="section-title">Tips &amp; hints</h2>
                <div class="tips-grid">
                    {#each tips as tip, i (tip.title)}
                        {@const tipAccent = accentFor(i)}
                        <a
                            use:link
                            href={tip.href}
                            class="tip-card"
                            style="--tip-accent: {accentVar(tipAccent)}; --tip-accent-soft: {accentVar(tipAccent, 'soft')}; --tip-accent-ink: {accentVar(tipAccent, 'ink')};"
                        >
                            <div class="tip-chip" aria-hidden="true">
                                <i class="{tip.icon} icon"></i>
                            </div>
                            <div class="tip-body">
                                <div class="tip-title">{tip.title}</div>
                                <div class="tip-text">{tip.body}</div>
                            </div>
                            <i class="angle right icon tip-chev" aria-hidden="true"></i>
                        </a>
                    {/each}
                </div>
            </section>
        {/if}
    {/if}
</div>

<style>
    .user-home { padding: var(--space-5) var(--space-6); }
    @media (max-width: 768px) { .user-home { padding: var(--space-4); } }

    .section { margin-bottom: var(--space-8); }
    .section-title {
        font-size: var(--text-sm);
        font-weight: 600;
        letter-spacing: .04em;
        text-transform: uppercase;
        color: var(--grey-secondary);
        margin: 0 0 var(--space-4) 0;
    }

    .quickstart-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
        gap: var(--space-4);
    }
    .quickstart-card {
        display: flex;
        align-items: center;
        gap: var(--space-4);
        padding: var(--space-5);
        background: var(--surface);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        text-decoration: none;
        color: var(--grey);
        transition: border-color .15s, box-shadow .15s, transform .15s;
    }
    .quickstart-card:hover {
        border-color: var(--primary);
        box-shadow: var(--shadow-pop);
        transform: translateY(-2px);
        color: var(--grey);
    }
    .quickstart-card:focus-visible { outline: none; box-shadow: var(--focus-ring); }

    .qs-icon {
        width: 48px; height: 48px;
        display: flex; align-items: center; justify-content: center;
        background: var(--card-accent-soft, var(--status-info-soft));
        color: var(--card-accent-ink, var(--primary));
        border-radius: var(--radius-card);
        flex-shrink: 0;
    }
    .qs-icon :global(i.icon) { font-size: 1.35rem; margin: 0; }

    .qs-body { flex: 1; min-width: 0; }
    .qs-name { font-size: var(--text-lg); font-weight: 500; color: var(--grey); }
    .qs-subtitle { font-size: var(--text-sm); color: var(--grey-secondary); margin-top: 2px; }

    .qs-cta {
        display: inline-flex;
        align-items: center;
        gap: var(--space-1);
        color: var(--primary);
        font-weight: 500;
        font-size: var(--text-sm);
        white-space: nowrap;
    }
    .quickstart-card:hover .qs-cta :global(i.icon) { transform: translateX(2px); }
    .qs-cta :global(i.icon) { transition: transform .15s; margin: 0; }

    .stat-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: var(--space-4);
    }
    .stat-card {
        display: flex; flex-direction: column; gap: var(--space-2);
        background: var(--surface);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: var(--space-5);
        text-align: left;
        cursor: pointer;
        font: inherit;
        color: inherit;
        transition: border-color .15s, box-shadow .15s, transform .15s;
    }
    .stat-card:hover { border-color: var(--primary); box-shadow: var(--shadow-pop); transform: translateY(-2px); }
    .stat-card:focus-visible { outline: none; box-shadow: var(--focus-ring); }
    .stat-card.summary { cursor: default; background: var(--status-info-soft); border-color: var(--primary); }
    .stat-card.summary:hover { transform: none; box-shadow: var(--shadow-card); }

    .stat-head { display: inline-flex; align-items: center; gap: var(--space-2); }
    .stat-head :global(i.icon) { color: var(--primary); font-size: var(--text-base); margin: 0; }
    .stat-card .stat-icon { color: var(--card-accent, var(--primary)) !important; }
    .stat-label { font-size: var(--text-sm); color: var(--grey-secondary); font-weight: 500; }
    .stat-value {
        font-size: var(--text-3xl);
        font-weight: 600;
        color: var(--grey);
        font-variant-numeric: tabular-nums;
        line-height: 1;
    }
    .stat-hint { font-size: var(--text-xs); color: var(--grey-secondary); }

    .split {
        display: grid;
        grid-template-columns: minmax(320px, 1fr) minmax(360px, 1.2fr);
        gap: var(--space-6);
    }
    @media (max-width: 900px) {
        .split { grid-template-columns: 1fr; }
    }

    .recent-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: var(--space-2); }
    .recent-row {
        display: flex; align-items: center; gap: var(--space-3);
        padding: var(--space-3) var(--space-4);
        background: var(--surface);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-card);
        text-decoration: none;
        color: var(--grey);
        transition: background-color .15s, border-color .15s;
    }
    .recent-row:hover { background: var(--surface-hover); border-color: var(--primary); color: var(--grey); }
    .recent-row:focus-visible { outline: none; box-shadow: var(--focus-ring); }

    .recent-icon {
        width: 36px; height: 36px;
        display: flex; align-items: center; justify-content: center;
        background: var(--row-accent-soft, var(--status-info-soft));
        color: var(--row-accent-ink, var(--primary));
        border-radius: var(--radius-card);
        flex-shrink: 0;
    }
    .recent-icon :global(i.icon) { margin: 0; font-size: 1rem; }

    .recent-main { flex: 1; min-width: 0; }
    .recent-title {
        font-size: var(--text-base);
        font-weight: 500;
        color: var(--grey);
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .recent-meta {
        display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-2);
        font-size: var(--text-xs);
        color: var(--grey-secondary);
        margin-top: 2px;
    }
    .recent-agent { font-weight: 500; color: var(--row-accent-ink, var(--primary)); }
    .recent-time {
        font-size: var(--text-xs);
        color: var(--grey-secondary);
        white-space: nowrap;
        flex-shrink: 0;
        font-variant-numeric: tabular-nums;
    }

    .empty-muted {
        padding: var(--space-5);
        text-align: center;
        background: var(--surface);
        border: 1px dashed var(--border-color);
        border-radius: var(--radius-card);
        color: var(--grey-secondary);
        font-size: var(--text-sm);
    }

    .chart-wrap {
        background: var(--surface);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-card);
        padding: var(--space-4);
        height: 260px;
    }

    .tips-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: var(--space-4);
    }
    .tip-card {
        display: flex; align-items: flex-start; gap: var(--space-3);
        padding: var(--space-4);
        background: var(--surface);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-card);
        text-decoration: none;
        color: var(--grey);
        transition: border-color .15s, box-shadow .15s;
    }
    .tip-card:hover {
        border-color: var(--tip-accent, var(--primary));
        box-shadow: var(--shadow-card);
        color: var(--grey);
    }
    .tip-card:focus-visible { outline: none; box-shadow: var(--focus-ring); }

    .tip-chip {
        width: 36px; height: 36px;
        display: flex; align-items: center; justify-content: center;
        background: var(--tip-accent-soft, var(--status-info-soft));
        color: var(--tip-accent-ink, var(--primary));
        border-radius: var(--radius-card);
        flex-shrink: 0;
    }
    .tip-chip :global(i.icon) { font-size: 1.1rem; margin: 0; }

    .tip-body { flex: 1; min-width: 0; }
    .tip-title { font-size: var(--text-sm); font-weight: 600; color: var(--grey); }
    .tip-text { font-size: var(--text-xs); color: var(--grey-secondary); margin-top: 2px; line-height: 1.5; }
    .tip-chev { color: var(--grey-secondary) !important; align-self: center; margin: 0 !important; }
</style>
